/**
 * FillInTheBlank drill strategy + kind helpers (Phase 3, design §1).
 * Pure: no Vue, no i18n, no effects. Ported from the FITB composables;
 * placeholders are replaced at every occurrence, literally (BUG-20 fixed).
 */
import type {
  DrillFillInTheBlank,
  StackBlank,
  StackFillInTheBlankItem,
  StackWrongChoice,
} from 'src/types/components';
import type { Try } from 'src/types/course/step';
import { orderArray } from 'src/shared/utilities/array';
import { normalizeApostrophes } from 'src/shared/utilities/string';
import { escapeHtml } from 'src/shared/utilities/string';
import { escapeText } from './escapeText';
import type { DrillResultRowMap, DrillStrategy, FitbResult } from './types';

type FitbItem = StackFillInTheBlankItem['attributes'];
type Blank = StackBlank['attributes'];

/**
 * Every occurrence of `search` replaced, literally (no `$&`/`$1` patterns;
 * BUG-20). An empty search changes nothing.
 */
function replaceEvery(text: string, search: string, replacement: string) {
  if (!search) return text;
  return text.split(search).join(replacement);
}

/** The sentence with every placeholder (every occurrence) replaced by its correct word. */
export function fitbCorrectSentence(item: FitbItem | undefined): string {
  return ((item?.blank_item ?? []) as Blank[]).reduce(
    (acc: string, blank: Blank) =>
      replaceEvery(acc, blank.placeholder, String(blank.correct)),
    item?.sentence ?? '',
  );
}

/** The sentence with every placeholder replaced by '__'. */
export function fitbBlankedSentence(item: FitbItem | undefined): string {
  return ((item?.blank_item ?? []) as Blank[]).reduce(
    (acc: string, blank: Blank) => replaceEvery(acc, blank.placeholder, '__'),
    item?.sentence ?? '',
  );
}

/**
 * "Your answer" HTML: the sentence with each answer filled in, wrong ones in
 * red. Sentence and answers are HTML-escaped (BUG-21); placeholders are
 * matched in the escaped sentence.
 */
export function fitbFeedbackHtml(
  sentence: string,
  result: FitbResult | string,
) {
  if (typeof result === 'string') return escapeHtml(result);
  const htmlSentence = `<p class="text-positive q-my-none">${escapeText(sentence ?? '')}</p>`;
  if (!result) return htmlSentence;
  return Object.keys(result)?.reduce((acc: string, placeholder: string) => {
    return replaceEvery(
      acc,
      escapeHtml(placeholder),
      `${result?.[placeholder]?.isCorrect ? '' : '<span class="text-negative">'}${escapeText(result?.[placeholder]?.answer ?? '')}${result?.[placeholder]?.isCorrect ? '' : '</span>'}`,
    );
  }, htmlSentence);
}

/** The item's placeholders in blank order ([] when blank_item is missing). */
export function fitbPlaceholders(item: FitbItem | undefined): string[] {
  return item?.blank_item?.map((bi: Blank) => bi.placeholder) ?? [];
}

/** The sentence split on single spaces, each word trimmed. */
export function fitbSentenceWords(item: FitbItem | undefined): string[] {
  return item?.sentence?.split(' ').map((s: string) => s.trim()) ?? [];
}

export function fitbBlankItem(item: FitbItem | undefined, placeholder: string) {
  return (item?.blank_item as Blank[] | undefined)?.find(
    (bi: Blank) => bi.placeholder === placeholder,
  );
}

/** Dropdown options of one blank: wrong choices + the correct one, shuffled (1 orderArray call). */
export function fitbBlankChoices(
  item: FitbItem | undefined,
  placeholder: string,
) {
  const blankItem = fitbBlankItem(item, placeholder);
  const choices: string[] =
    blankItem?.wrong_choice?.map(
      (c: StackWrongChoice['attributes']) => c.wrong_choice as string,
    ) ?? [];

  if (blankItem) choices.push(blankItem.correct);
  return orderArray<string>(choices, 'random');
}

/** The solve card's "available answers" list (Type + show_availiable_choices only). */
export function fitbAvailableChoices(
  item: FitbItem | undefined,
  drillType: DrillFillInTheBlank['attributes'] | undefined,
) {
  if (
    !item ||
    !drillType?.show_availiable_choices ||
    drillType?.reply_method !== 'Type'
  )
    return [];

  const blankItem: Blank[] = item?.blank_item;

  const correctChoices = blankItem
    ? [
        ...blankItem.reduce((acc: Set<string>, curr: Blank) => {
          acc.add(curr?.correct);
          return acc;
        }, new Set()),
      ]
    : [];

  const wrongChoices = orderArray(
    blankItem
      ? [
          ...blankItem.reduce((acc: Set<string>, curr: Blank) => {
            (
              curr?.wrong_choice?.map(
                (c: StackWrongChoice['attributes']) => c.wrong_choice,
              ) ?? []
            ).forEach((c: string) => acc.add(c));

            return acc;
          }, new Set()),
        ]
      : [],
    'random',
  );

  return orderArray([...correctChoices, ...wrongChoices], 'random');
}

/** One blank's check: apostrophe-normalised, case-insensitive, trimmed. */
export function fitbIsBlankCorrect(answer: string, correct: string) {
  return (
    normalizeApostrophes(answer).toLocaleLowerCase().trim() ===
    normalizeApostrophes(correct).toLocaleLowerCase().trim()
  );
}

/**
 * The solve card's verdict. Quirk kept: it compares the NUMBER of result keys
 * with the number of placeholders (a duplicated placeholder can never pass).
 */
export function fitbIsComplete(
  result: FitbResult | undefined,
  item: FitbItem | undefined,
) {
  return !!(
    result &&
    fitbPlaceholders(item).length === Object.keys(result).length &&
    !Object.keys(result)?.some((key) => !result?.[key]?.isCorrect)
  );
}

/** The drill card's text: the sentence with each filled answer in place (empty blanks dropped). */
function questionText(item: FitbItem | undefined, result?: FitbResult) {
  const words = fitbSentenceWords(item);
  // The drill card leaves its text untouched here; '' is its initial value.
  if (!words?.length) return '';
  const placeholders = fitbPlaceholders(item);
  return words
    .reduce((acc, word) => {
      if (placeholders.includes(word)) {
        return result?.[word]?.answer
          ? `${acc} ${result?.[word]?.answer}`
          : acc;
      } else {
        return `${acc} ${word}`;
      }
    }, '')
    .trim();
}

function correctAnswer(item: FitbItem | undefined) {
  return item &&
    typeof item === 'object' &&
    'blank_item' in item &&
    'sentence' in item
    ? fitbCorrectSentence(item)
    : null;
}

const errorRow: DrillResultRowMap['FillInTheBlank'] = {
  id: -1,
  sentence: undefined,
  source: undefined,
  correct: undefined,
  tries: [],
};

export const fillInTheBlankStrategy: DrillStrategy<'FillInTheBlank'> = {
  kind: 'FillInTheBlank',
  speaksQuestion: false,
  speaksAnswer: true,
  errorRow,
  correctAnswer(item) {
    return correctAnswer(item);
  },
  isCorrect(answer) {
    if (answer === undefined || answer === null) return false;
    return !!answer?.isCorrect;
  },
  feedbackAnswerHtml(answer, item) {
    // Destructures like today: an undefined answer throws.
    const { result } = answer;
    const sentence =
      item && 'sentence' in item ? ((item.sentence as string) ?? '') : '';
    return fitbFeedbackHtml(sentence, result ?? {});
  },
  // Rendered via v-html: escaped (BUG-21).
  feedbackCorrectAnswer(item) {
    return escapeText(correctAnswer(item));
  },
  feedbackSubtitle() {
    return '';
  },
  helpAnswer(item) {
    return escapeText(correctAnswer(item));
  },
  spokenAnswer(_answer, questionText) {
    // Spoken text: typed blanks may carry stray whitespace (BUG-33).
    return questionText?.replace(/\s+/g, ' ').trim();
  },
  questionText(item, _ctx, fitbResult) {
    return questionText(item, fitbResult);
  },
  resultRow(item, id, tries: Try[]) {
    return {
      id,
      sentence: item.sentence,
      source: fitbBlankedSentence(item),
      correct: fitbCorrectSentence(item),
      tries,
    };
  },
};
